import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Mail, RefreshCw, Heart } from 'lucide-react';
interface AuthFormProps {
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  firstName: string;
  setFirstName: (firstName: string) => void;
  familyRole: string;
  setFamilyRole: (role: string) => void;
  isLoading: boolean;
  showConfirmation: boolean;
  resendLoading: boolean;
  invitationToken?: string | null;
  invitationDetails?: any;
  handleSignIn: (e: React.FormEvent) => void;
  handleSignUp: (e: React.FormEvent) => void;
  handleGoogleSignIn: () => void;
  handleAppleSignIn: () => void;
  handleResendConfirmation: () => void;
  setShowConfirmation: (show: boolean) => void;
}
export const AuthForm = ({
  email,
  setEmail,
  password,
  setPassword,
  firstName,
  setFirstName,
  familyRole,
  setFamilyRole,
  isLoading,
  showConfirmation,
  resendLoading,
  invitationToken,
  invitationDetails,
  handleSignIn,
  handleSignUp,
  handleGoogleSignIn,
  handleAppleSignIn,
  handleResendConfirmation,
  setShowConfirmation
}: AuthFormProps) => {
  const [activeTab, setActiveTab] = useState("signup");
  if (showConfirmation) {
    return <div className="text-center space-y-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <Mail className="h-8 w-8 text-green-600 mx-auto mb-2" />
          <h3 className="font-semibold text-green-800 mb-2">Vérifiez votre email</h3>
          <p className="text-green-700 text-sm mb-4">
            Un email de confirmation a été envoyé à <strong>{email}</strong>
          </p>
          <p className="text-green-600 text-sm">
            Cliquez sur le lien dans l'email pour activer votre compte.
          </p>
        </div>
        
        <div className="space-y-3">
          <Button variant="outline" onClick={handleResendConfirmation} disabled={resendLoading} className="w-full">
            {resendLoading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
            Renvoyer l'email
          </Button>
          
          <Button variant="ghost" onClick={() => setShowConfirmation(false)} className="w-full">
            Retour au formulaire
          </Button>
        </div>
      </div>;
  }
  return <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="signin">Connexion</TabsTrigger>
          <TabsTrigger value="signup">Inscription</TabsTrigger>
        </TabsList>
        
        <TabsContent value="signin">
          <div className="space-y-4">
            <Button onClick={handleGoogleSignIn} variant="outline" className="w-full h-12 text-base" disabled={isLoading}>
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continuer avec Google
            </Button>

            <Button onClick={handleAppleSignIn} className="w-full h-12 text-base bg-black hover:bg-black/90 text-white border-0" disabled={isLoading}>
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              Continuer avec Apple
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator className="w-full" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">ou</span>
              </div>
            </div>

            <form onSubmit={handleSignIn} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="signin-email">Email</Label>
                <Input id="signin-email" type="email" placeholder="votre@email.com" value={email} onChange={e => setEmail(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="signin-password">Mot de passe</Label>
                <Input id="signin-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required />
              </div>
              <Button type="submit" className="w-full h-12 text-base bg-[hsl(var(--violet-primary))] hover:bg-[hsl(var(--violet-primary))]/90 text-white" disabled={isLoading}>
                {isLoading ? "Connexion..." : "Se connecter"}
              </Button>
            </form>
            
            {/* Navigation link */}
            <div className="text-center mt-4">
              <p className="text-sm text-muted-foreground">
                Pas encore inscrit ?{' '}
                <button 
                  type="button"
                  onClick={() => setActiveTab('signup')}
                  className="text-[hsl(var(--violet-primary))] hover:underline font-medium"
                >
                  Inscrivez-vous
                </button>
              </p>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="signup">
          <div className="space-y-4">
            <Button onClick={handleGoogleSignIn} variant="outline" className="w-full h-12 text-base" disabled={isLoading}>
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continuer avec Google
            </Button>

            <Button onClick={handleAppleSignIn} className="w-full h-12 text-base bg-black hover:bg-black/90 text-white border-0" disabled={isLoading}>
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              Continuer avec Apple
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator className="w-full" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">ou</span>
              </div>
            </div>

            <form onSubmit={handleSignUp} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="signup-firstname">Prénom</Label>
                <Input id="signup-firstname" type="text" placeholder="Prénom" value={firstName} onChange={e => setFirstName(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="signup-email">Email</Label>
                <Input id="signup-email" type="email" placeholder="votre@email.com" value={email} onChange={e => setEmail(e.target.value)} disabled={!!invitationToken} required />
              </div>
              {invitationToken && invitationDetails?.personDetails?.category === 'family' && <div className="space-y-2">
                  <Label htmlFor="family-role">Rôle dans la famille</Label>
                  <Select value={familyRole} onValueChange={setFamilyRole}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner votre rôle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="parent">Parent</SelectItem>
                      <SelectItem value="spouse">Conjoint(e)</SelectItem>
                      <SelectItem value="son">Fils</SelectItem>
                      <SelectItem value="daughter">Fille</SelectItem>
                      <SelectItem value="child">Enfant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>}
              <div className="space-y-2">
                <Label htmlFor="signup-password">Mot de passe</Label>
                <Input id="signup-password" type="password" placeholder="Minimum 6 caractères" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} />
              </div>
              <Button type="submit" className="w-full h-12 text-base bg-[hsl(var(--violet-primary))] hover:bg-[hsl(var(--violet-primary))]/90 text-white" disabled={isLoading}>
                {isLoading ? "Création du compte..." : "Créer mon compte"}
              </Button>
            </form>
            
            {/* Navigation link */}
            <div className="text-center mt-4">
              <p className="text-sm text-muted-foreground">
                Déjà inscrit ?{' '}
                <button 
                  type="button"
                  onClick={() => setActiveTab('signin')}
                  className="text-[hsl(var(--violet-primary))] hover:underline font-medium"
                >
                  Connectez-vous
                </button>
              </p>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <div className="text-center">
        
      </div>
    </div>;
};